import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import './PrivacyPolicy.css';

function PrivacyChoices() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [optOutOfLeads, setOptOutOfLeads] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [gpcDetected, setGpcDetected] = useState(false);

  useEffect(() => {
    // Check for Global Privacy Control signal
    if (navigator.globalPrivacyControl) {
      setGpcDetected(true);
    }

    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      setIsLoggedIn(true);
      fetchOptOutStatus();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchOptOutStatus = async () => {
    try {
      const response = await axiosInstance.get('/leads-opt-out/');
      setOptOutOfLeads(response.data.opt_out_of_leads);
    } catch (err) {
      console.error('Error fetching opt-out status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptOutToggle = async () => {
    const newValue = !optOutOfLeads;
    setIsSaving(true);
    setMessage('');

    try {
      const response = await axiosInstance.post('/leads-opt-out/', {
        opt_out: newValue
      });
      setOptOutOfLeads(response.data.opt_out_of_leads);
      setMessage(response.data.message);
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Error updating opt-out status:', err);
      setMessage('Failed to update preference. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="privacy-policy-container">
      <h1>Your Privacy Choices</h1>
      <p><strong>Do Not Sell or Share My Personal Information</strong></p>

      {/* GPC Detection Notice */}
      {gpcDetected && (
        <div style={{
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <strong>Global Privacy Control Detected</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            We detected a Global Privacy Control (GPC) signal from your browser.
            Your sale/share opt-out preference is automatically honored for this session
            as required by California and other state privacy laws.
          </p>
        </div>
      )}

      <h2>What is IQ Intro?</h2>
      <p>
        IQ Intro is our service that helps private golf clubs discover prospective members.
        When enabled, clubs may see limited, non-identifying information about you—such as
        your age range, income band, household composition, and proximity to the club—to
        determine if you might be a good fit for membership.
      </p>
      <p>
        <strong>Important:</strong> Your name, email, and phone number are never shared
        with clubs unless you explicitly accept a club's request to connect.
      </p>

      <h2>Your Opt-Out Rights</h2>
      <p>
        Under state privacy laws including the California Consumer Privacy Act (CCPA/CPRA),
        Colorado Privacy Act (CPA), and Texas Data Privacy and Security Act (TDPSA),
        showing user-level attributes to clubs may be considered a "sale" or "share" of
        personal information. You have the right to opt out of this at any time.
      </p>

      {/* Opt-Out Toggle Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '1.5rem',
        marginTop: '2rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ marginTop: 0 }}>IQ Intro Opt-Out</h3>

        {isLoading ? (
          <p>Loading your preferences...</p>
        ) : isLoggedIn ? (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}>
              <div>
                <strong>IQ Intro Status:</strong>
                <span style={{
                  marginLeft: '0.5rem',
                  color: optOutOfLeads ? '#dc3545' : '#28a745',
                  fontWeight: 'bold'
                }}>
                  {optOutOfLeads ? 'Opted Out (Not Shared)' : 'Opted In (Active)'}
                </span>
              </div>
              <button
                onClick={handleOptOutToggle}
                disabled={isSaving}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: optOutOfLeads ? '#28a745' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1
                }}
              >
                {isSaving ? 'Saving...' : (optOutOfLeads ? 'Opt Back In' : 'Opt Out')}
              </button>
            </div>

            {message && (
              <p style={{
                padding: '0.75rem',
                backgroundColor: message.includes('Failed') ? '#f8d7da' : '#d4edda',
                borderRadius: '4px',
                marginTop: '0.5rem'
              }}>
                {message}
              </p>
            )}

            <p style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: 0 }}>
              {optOutOfLeads
                ? 'Your data will not be shown to clubs through IQ Intro. You can opt back in at any time.'
                : 'Clubs can see your anonymized profile attributes. Your contact info is never shared without your explicit consent.'}
            </p>
          </>
        ) : (
          <>
            <p>
              <strong>Not logged in.</strong> IQ Intro only applies to PrivateIQ account holders.
              If you don't have an account, there's nothing to opt out of—we don't have your data.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#0073aa',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '1rem'
              }}
            >
              Log In to Manage Preferences
            </button>
            <button
              onClick={() => navigate('/register')}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: 'transparent',
                color: '#0073aa',
                border: '1px solid #0073aa',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Create Account
            </button>
          </>
        )}
      </div>

      <h2>Other Ways to Manage Your Privacy</h2>
      <ul>
        <li>
          <strong>iOS App:</strong> Go to Settings &rarr; Account Settings &rarr; Privacy Preferences &rarr; IQ Intro (toggle off)
        </li>
        <li>
          <strong>Global Privacy Control (GPC):</strong> We honor GPC signals from your browser.
          When detected, we automatically treat it as an opt-out of sale/share for your session.
        </li>
        <li>
          <strong>Access, Correct, or Delete Your Data:</strong> Email us at{' '}
          <a href="mailto:support@privateIQ.golf">support@privateIQ.golf</a>
        </li>
      </ul>

      <h2>Questions?</h2>
      <p>
        For more details about how we collect, use, and protect your information,
        please read our full <Link to="/privacypolicy">Privacy Policy</Link>.
      </p>
      <p>
        If you have questions or wish to exercise any privacy rights, contact us at:
      </p>
      <address>
        <strong>Non-Target LLC</strong><br />
        5511 Parkcrest Drive, Suite 103, #1000<br />
        Austin, TX 78731<br />
        United States<br />
        <a href="mailto:support@privateIQ.golf">support@privateIQ.golf</a>
      </address>
    </div>
  );
}

export default PrivacyChoices;